/**
 * Where sign-in lives, and which requests must never be treated as an expired
 * session.
 *
 * This module holds no React and imports nothing from the app. That is the
 * point: the transport layer and the auth pages both need these facts, and if
 * either owned them the other would have to import it — `services/api.ts`
 * pulling in a page module, or a page reaching into the transport. Both sides
 * depend on this file; it depends on neither.
 *
 *     DialerLogin / App  →  authRoutes  ←  services/api
 */

export const AUTH_ROUTES = {
  defaultLogin: '/login',
  dialerLogin: '/portal/dialer/login',
} as const;

/**
 * Requests that ESTABLISH a credential rather than spend one.
 *
 * A 401 from one of these means "those credentials are wrong" — it does not
 * mean "your session expired", and trying to refresh a session on their behalf
 * is incoherent: there is no session yet. Doing it anyway is what produced
 * D-1. A failed sign-in entered the refresh branch, posted a null refresh
 * token, got a 400 back from `/auth/refresh`, and the caller was handed that
 * 400 in place of the real 401 — so every consumer downstream, including the
 * `status` Phase 0 works hard to preserve, was reading an unrelated endpoint's
 * answer. It also spent a second token against the credential rate limiter and
 * navigated the browser away from whichever sign-in page the user was on.
 *
 * Mirrors `CREDENTIAL_PATHS` in `backend/src/server.js`, which decides the same
 * question for the rate limiter. The two lists must not drift: a path the
 * server treats as credential-establishing and the client does not is exactly
 * the gap D-1 lived in.
 */
export const NO_SESSION_REFRESH_PATHS: ReadonlySet<string> = new Set([
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/google',
  '/auth/verify-email',
  '/auth/resend-verification',
  '/auth/refresh',
]);

/**
 * Strip a request URL down to a comparable path.
 *
 * axios hands the interceptor whatever the caller passed, which may be a bare
 * path, a path with a query string, or an absolute URL once `baseURL` has been
 * applied. Comparing raw `config.url` against the set above would therefore
 * miss `/auth/login?next=x` and any absolute form — so the comparison is made
 * on a normalized pathname instead.
 */
export function normalizeRequestPath(url?: string): string {
  if (!url) return '';
  let path = url;

  // Absolute URL → take the pathname. (Parsed against a dummy origin so a
  // relative path is handled by the same code without branching.)
  try {
    path = new URL(url, 'http://x').pathname;
  } catch {
    path = url.split('?')[0].split('#')[0];
  }

  return path;
}

/**
 * Is this request establishing a credential rather than spending one?
 *
 * Suffix match, because the path may arrive carrying the API base
 * (`/api/v1/auth/login`) or bare (`/auth/login`). The members of the set are
 * specific enough that a suffix match cannot collide with an unrelated route.
 */
export function isCredentialPath(url?: string): boolean {
  const path = normalizeRequestPath(url);
  if (!path) return false;
  for (const credential of Array.from(NO_SESSION_REFRESH_PATHS)) {
    if (path === credential || path.endsWith(credential)) return true;
  }
  return false;
}

/** Is the browser already sitting on a sign-in surface? */
export function isLoginSurface(pathname: string): boolean {
  return (
    pathname === AUTH_ROUTES.defaultLogin ||
    pathname === AUTH_ROUTES.dialerLogin ||
    pathname === '/forgot-password' ||
    pathname === '/reset-password'
  );
}

/**
 * The sign-in surface that belongs to where the user actually is.
 *
 * A calling assistant whose session expires mid-shift should land back on the
 * Dialer's own sign-in, not the investor portal's. Before this existed the
 * destination was the literal `'/login'`, so a workspace that had its own
 * sign-in silently sent its users somewhere else.
 */
export function loginRouteFor(pathname: string): string {
  return pathname.startsWith('/portal/dialer') ? AUTH_ROUTES.dialerLogin : AUTH_ROUTES.defaultLogin;
}

/**
 * Seam for navigation, so this is testable and so the redirect has exactly one
 * implementation. Mirrors `dialerLoginNav` in the dialer, which exists for the
 * same reason: jsdom cannot navigate.
 */
export const authNav = {
  go: (path: string): void => {
    window.location.href = path;
  },
};

/**
 * Everything a sign-out must not leave behind.
 *
 * There were three divergent lists. `UserContext.logout` cleared two keys,
 * `AuthContext.logout` cleared three, and the 401 interceptor cleared a fourth
 * combination — so signing out of one context left the `refreshToken` that the
 * interceptor reads, and signing out while impersonating left an admin's token
 * sitting in localStorage for the next person at that machine. Two contexts
 * coexist during the migration; that is a reason for ONE cleanup contract, not
 * three approximations of one.
 *
 * `investAccessToken` is here because the request interceptor attaches it to
 * every request as `X-Invest-Access-Token`: it gates private offerings, so it
 * is credential-shaped even though it lives in sessionStorage.
 *
 * Deliberately NOT cleared:
 *  - `sandboxPayments` — an admin's UI mode toggle, read by four components and
 *    by `isSandboxAdmin`. Not a credential. Clearing it on sign-out would flip
 *    a setting the person chose, silently.
 *  - `google_oauth_state` — a transient OAuth nonce. Clearing it mid-handshake
 *    breaks the handshake and buys nothing.
 */
export const AUTH_STORAGE_KEYS = {
  local: [
    'authToken',
    'refreshToken',
    'tokenExpiry',
    'impersonating_as',
    'original_admin_token',
  ],
  session: ['investAccessToken'],
} as const;

/**
 * A return destination we are willing to send someone to after an interruption.
 *
 * Sign-in and email verification both interrupt a journey and owe the person
 * the place they were going. Carrying that destination is also the classic
 * open-redirect shape, so it is validated rather than trusted.
 *
 * A bare `startsWith('/')` is NOT sufficient: `//evil.com` and `/\evil.com` both
 * begin with a slash and are resolved by browsers as protocol-relative URLs
 * pointing off-origin. Anything with a scheme or an authority is refused here.
 *
 * Both carriers now go through this — `VerifyEmailRequired` (D-4) and
 * `AuthFlow`'s post-sign-in redirect, which held the bare check until it was
 * replaced with this call. `safeReturnPath.test.ts` asserts that neither
 * reintroduces the shape.
 */
export function safeReturnPath(candidate: unknown): string | null {
  if (typeof candidate !== 'string' || !candidate) return null;
  // Must be a rooted path, and must not be protocol-relative in either slash form.
  if (!candidate.startsWith('/')) return null;
  if (candidate.startsWith('//') || candidate.startsWith('/\\')) return null;
  // No scheme, no authority, no control characters smuggled through.
  if (/[\u0000-\u001f\u007f]/.test(candidate)) return null;
  if (/^\/+[a-z][a-z0-9+.-]*:/i.test(candidate)) return null;
  return candidate;
}

/**
 * The query string of a carried destination, or '' (2026-10-19: the morning email's links carry ?view=day&date=...,
 * ?view=manifest&lens=atd; dropping the query sent a signed-out reader to the wrong view after sign-in).
 *
 * A query cannot move the destination off this origin -- it is appended after a path safeReturnPath already
 * accepted -- so it is kept whole, but only in its plain shape: it starts with '?', holds no control character and
 * no backslash, and is at most 2048 characters. Anything else is dropped (the path is still returned to).
 */
export function safeReturnSearch(search: unknown): string {
  if (typeof search !== 'string' || !search || search === '?') return '';
  if (!search.startsWith('?') || search.length > 2048) return '';
  // eslint-disable-next-line no-control-regex -- refusing control characters is the point
  if (/[\x00-\x1f\x7f\\]/.test(search)) return '';
  return search;
}

/**
 * A carried destination (react-router's location: { pathname, search }) as one path to navigate to, or null.
 * The path goes through safeReturnPath, and is also refused when it begins with an encoded slash or backslash
 * (/%2F%2Fevil.example, /%5Cevil.example), which some layers decode into a protocol-relative URL. The query goes
 * through safeReturnSearch. The fragment is not carried.
 */
export function safeReturnTarget(from: unknown): string | null {
  if (!from || typeof from !== 'object') return null;
  const { pathname, search } = from as { pathname?: unknown; search?: unknown };
  const path = safeReturnPath(pathname);
  if (!path) return null;
  if (/^\/+(%2f|%5c)/i.test(path)) return null;
  if (/[?#]/.test(path)) return path.split(/[?#]/)[0] || null;
  return `${path}${safeReturnSearch(search)}`;
}

/** The one sign-out cleanup. Both contexts and the transport layer call this. */
export function clearAuthStorage(): void {
  for (const key of AUTH_STORAGE_KEYS.local) {
    try { localStorage.removeItem(key); } catch { /* storage unavailable */ }
  }
  for (const key of AUTH_STORAGE_KEYS.session) {
    try { sessionStorage.removeItem(key); } catch { /* storage unavailable */ }
  }
}

/**
 * Per-person working state that holds PHI, by key prefix: the Patient
 * Advocate's unsent note drafts and outbox (pages/advocate/noteStorage,
 * 2026-10-05). Each key also carries the signed-in user's id, and only that
 * user's keys are ever read.
 */
export const USER_SCOPED_STORAGE_PREFIXES: readonly string[] = ['pa.notes.'];

/**
 * One person's user-scoped keys in this browser: under a registered prefix and
 * carrying `:<userId>:` in the name (every such key does, e.g.
 * `pa.notes.v3:outbox:<userId>:<patientId>:<noteKey>:<day>`). Names only; nothing is read.
 */
export function userScopedKeysOf(userId: string | null | undefined): string[] {
  if (!userId) return [];
  let s: Storage;
  try { s = localStorage; } catch { return []; }
  try {
    const out: string[] = [];
    const mark = `:${userId}:`;
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (k && USER_SCOPED_STORAGE_PREFIXES.some((p) => k.startsWith(p)) && k.includes(mark)) out.push(k);
    }
    return out;
  } catch { return []; }
}

/**
 * Residents for whom this person still has unsent words here (a draft, or a
 * note not confirmed), counted by key NAME -- the sign-out asks before
 * deleting them (fix round 3: a sign-out used to delete them silently).
 */
export function unsentUserScopedCount(userId: string | null | undefined, now: number = Date.now()): number {
  // Only keys still live by their NAME (the day at the end, within the 7 days): an expired key
  // is dropped unread when a thread opens, so the person could not get it back by cancelling.
  const residents = new Set(userScopedKeysOf(userId)
    .filter((k) => userScopedKeyLive(k, now))
    .map((k) => k.split(':').slice(2, 4).join(':')));
  return residents.size;
}

/** How long a per-person key lives (noteStorage uses the same figure). */
export const USER_SCOPED_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * True when a key's name ends in a day within the last 7 (a day of slack, as noteStorage judges it).
 * Fix round 11: AGE ONLY, as noteStorage judges it since round 10. A name dated after today (the clock was
 * moved back after it was saved) is live: its words are still on this computer and a thread still shows
 * them, so a sign-out must ask before deleting them. The old upper bound (today + 1) made a sign-out after
 * the clock went back two or more days delete them without asking.
 */
export function userScopedKeyLive(k: string, now: number = Date.now()): boolean {
  const m = /:(\d{1,7})$/.exec(k);
  if (!m) return false;
  const day = Number(m[1]);
  return day >= Math.floor((now - USER_SCOPED_TTL_MS) / DAY_MS);
}

/**
 * Ask before a sign-out deletes unsent words. True means go ahead: nothing is
 * unsent, or the person said yes. Every sign-out button calls this first.
 */
export function confirmSignOutWithUnsent(userId: string | null | undefined): boolean {
  const n = unsentUserScopedCount(userId);
  if (n === 0) return true;
  try {
    return window.confirm(
      `You have unsent notes for ${n} resident${n === 1 ? '' : 's'} on this computer. `
      + 'Signing out deletes them. Choose Cancel to go back and send or copy them, or OK to sign out and delete them.'
    );
  } catch { return true; }
}

/**
 * A deliberate sign-out clears the signed-in person's OWN per-person working
 * state, and only hers. Not part of clearAuthStorage on purpose: the transport
 * layer calls that when a session EXPIRES, and an expiry must not destroy a
 * PA's unsent note -- the keys are scoped to her user id, so nobody else can
 * read them, and they expire after 7 days. Another person's keys are hers to
 * come back to (they expire on their own), so a sign-out by someone else, or
 * one whose user is not known, leaves them.
 *
 * Fix round 8: the sign-out first leaves a marker naming the person (by a hash,
 * holding only the time), THEN removes her keys. Another tab of hers that is
 * still open reads the marker before it writes anything back, so it never
 * restores the words this sign-out deleted (it heard the keys go, and used to
 * write its box back as if another tab had taken it).
 */
export function clearUserScopedStorage(userId?: string | null): void {
  let s: Storage;
  try { s = localStorage; } catch { return; }
  const marked = markSignedOut(userId);
  try {
    userScopedKeysOf(userId).forEach((k) => { try { s.removeItem(k); } catch { /* keep going */ } });
  } catch { /* storage unavailable */ }
  // Fix round 14: storage full (of her own notes, often) refused the marker; her keys are gone now, so there is room.
  // Without it, an open tab of hers could not tell this was a sign-out, and words held in its memory could come back.
  if (!marked) markSignedOut(userId);
}

/**
 * The sign-out marker (fix round 8): `pa.signedout.v1:<hash of the user id>`, its value the time of the
 * sign-out in ms. No name, no words. A page that was showing this person's work since BEFORE that time
 * stores nothing more for her (signedOutSince); a page opened after it (she signed in again) is unaffected,
 * so a forgotten marker can never stop a later session from keeping its notes. A session that merely
 * EXPIRES leaves no marker: an expiry must not cost a PA her unsent words.
 */
export const SIGNED_OUT_PREFIX = 'pa.signedout.v1:';
function userHash(userId: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < userId.length; i += 1) { h ^= userId.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(36);
}
export const signedOutKey = (userId: string): string => `${SIGNED_OUT_PREFIX}${userHash(userId)}`;
export function markSignedOut(userId: string | null | undefined, now: number = Date.now()): boolean {
  if (!userId) return false;
  try { localStorage.setItem(signedOutKey(userId), String(now)); return true; } catch { return false; /* storage full or unavailable */ }
}
/**
 * When this person last signed out deliberately in this browser (null: no marker).
 *
 * Fix round 11: a marker dated in the FUTURE (she signed out while this computer's clock ran ahead, and the
 * clock was put right since) is no time at all: the sign-out happened at some moment before now. It is
 * re-dated to just before now (and written back), so it never outlives its 7 days and a page opened after it
 * is not taken for one that was open when she signed out ("You signed out in another tab", nothing stored).
 */
export function signedOutAt(userId: string | null | undefined, now: number = Date.now()): number | null {
  if (!userId) return null;
  try {
    const v = Number(localStorage.getItem(signedOutKey(userId)));
    if (!(Number.isFinite(v) && v > 0)) return null;
    if (v > now) {
      const at = now - 1;
      try { localStorage.setItem(signedOutKey(userId), String(at)); } catch { /* read as re-dated anyway */ }
      return at;
    }
    return v;
  } catch { return null; }
}
/** True when this person signed out at or after `since` (the time a page began showing her work). */
export function signedOutSince(userId: string | null | undefined, since: number, now: number = Date.now()): boolean {
  const at = signedOutAt(userId, now);
  return at !== null && at >= since;
}
/**
 * Whose session is in this browser right now (fix round 9): the `userId` the session token was issued for
 * (the server signs every access token with it). null: no token. '': a token whose user cannot be read.
 * Read only to compare: the token is not verified here, and nothing is trusted on its word.
 */
export function sessionUserId(): string | null {
  let token: string | null;
  try { token = localStorage.getItem(AUTH_STORAGE_KEYS.local[0]); } catch { return null; }
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return '';
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))) as { userId?: unknown; id?: unknown; sub?: unknown };
    const id = claims.userId ?? claims.id ?? claims.sub;
    return typeof id === 'string' || typeof id === 'number' ? String(id) : '';
  } catch { return ''; }
}
/**
 * Is THIS person signed in here right now? A token of anyone else -- someone who signed in after her
 * sign-out -- is not her session; nor is one whose user cannot be read (it cannot be shown to be hers).
 */
export function hasSessionFor(userId: string | null | undefined): boolean {
  if (!userId) return false;
  return sessionUserId() === String(userId);
}
